import { EmptyState, SearchInput, SidebarContent, Table } from 'components';
import { CellsTypes } from 'constants/index';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Event as EventType, EventTask, Profile } from 'types';
import { getUTCDateFromDate } from 'utils';

import { SidebarContentWrapper, StyledSearchWrapper } from './styled';

const mockTasksTableConfig = {
  title: 'Tasks',
  headers: [
    {
      id: 'name',
      width: '100%',
      type: CellsTypes.TASK_WITH_SELECT,
      headerType: 'title',
    },
  ],
};

const mockMembersTableConfig = {
  title: 'Members',
  headers: [
    {
      id: 'name',
      width: '100%',
      type: CellsTypes.AVATAR_NAME,
      headerType: 'title',
    },
  ],
};

type FilteredTask = EventTask & { onClick: () => void; memberCount: number };

interface EventDetailsTasksProps {
  tasks: EventTask[];
  selectedTask: string;
  event: EventType;
  users: Profile[];
  date: Date;
  setSelectedTask: (id: string) => void;
}

const EventDetailsTasks: FC<EventDetailsTasksProps> = ({
  tasks = [],
  selectedTask,
  event,
  users,
  date,
  setSelectedTask,
}) => {
  const { t } = useTranslation();
  const [inputValue, setValue] = useState<string>('');
  const [taskUsers, setTaskUsers] = useState<Profile[]>([]);
  const [isLoadingTaskUsers, setIsLoadingTaskUsers] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLDivElement>(null);

  const getTaskMemberCount = (taskId: string) => {
    const subTaskReplies = event.subEventTaskReplies
      ?.filter((t) => t.eventTaskId === taskId)
      .filter(
        (t) =>
          new Date(t.date).getTime() ===
          getUTCDateFromDate(new Date(date)).getTime(),
      );
    const filteredTaskReplies = subTaskReplies?.filter(
      (task, i, self) => i === self.findIndex((t) => t.userId === task.userId),
    );

    return filteredTaskReplies?.length || 0;
  };

  const [filteredTasks, setTasks] = useState<FilteredTask[]>(
    tasks.map((t) => ({
      ...t,
      onClick: () => setSelectedTask(t.id),
      memberCount: getTaskMemberCount(t.id),
    })),
  );

  const handleChange = (text) => {
    const newTasks = tasks.filter(({ title }) =>
      `${title}`.toLocaleLowerCase().includes(text.toLowerCase()),
    );
    setTasks(
      newTasks.map((t) => ({
        ...t,
        onClick: () => setSelectedTask(t.id),
        memberCount: getTaskMemberCount(t.id),
      })),
    );
    setValue(text);
  };

  useEffect(() => {
    if (!selectedTask) {
      setTaskUsers([]);
      setIsLoadingTaskUsers(false);
      return;
    }

    setIsLoadingTaskUsers(true);

    const timer = setTimeout(() => {
      const subTaskReplies = event.subEventTaskReplies
        ?.filter((t) => t.eventTaskId === selectedTask)
        .filter(
          (t) =>
            new Date(t.date).getTime() ===
            getUTCDateFromDate(new Date(date)).getTime(),
        );
      const filteredTaskReplies = subTaskReplies?.filter(
        (task, i, self) =>
          i === self.findIndex((t) => t.userId === task.userId),
      );

      setTaskUsers(
        users.filter((u) =>
          filteredTaskReplies.some((r) => r.userId === u.userId),
        ),
      );
      setIsLoadingTaskUsers(false);
    }, 50);

    return () => clearTimeout(timer);
  }, [selectedTask, event.subEventTaskReplies, users, date]);

  return (
    <SidebarContent
      height={'calc(100svh - 206px)'}
      smHeight={'calc(100svh - 170px)'}
    >
      {selectedTask ? (
        isLoadingTaskUsers ? (
          <EmptyState
            icon="⏳"
            title={t('statistics.loadingStatistics', 'Loading...')}
          />
        ) : taskUsers.length > 0 ? (
          <Table data={taskUsers} config={mockMembersTableConfig} />
        ) : (
          <EmptyState
            icon="👥"
            title={t('community.noMembersEmptyState.title')}
            description={t('community.noMembersEmptyState.description')}
          />
        )
      ) : (
        <>
          <StyledSearchWrapper ref={searchInputRef}>
            <SearchInput
              width="100%"
              value={inputValue}
              fillWidth={true}
              onChange={({ target }) => handleChange(target.value)}
            />
          </StyledSearchWrapper>
          <SidebarContentWrapper>
            {filteredTasks.length > 0 ? (
              <Table
                data={filteredTasks}
                config={mockTasksTableConfig}
                search={inputValue}
              />
            ) : inputValue.trim() ? (
              <EmptyState
                icon="🔍"
                title={t('event.noSearchResults.title')}
                description={t('event.noSearchResults.description', {
                  searchTerm: `"${inputValue.trim()}"`,
                }).replace(/&quot;/g, '"')}
                onClearSearch={() => {
                  handleChange('');
                  if (searchInputRef.current) {
                    const input = searchInputRef.current.querySelector('input');
                    if (input) {
                      input.value = '';
                      input.dispatchEvent(
                        new Event('input', { bubbles: true }),
                      );
                    }
                  }
                }}
              />
            ) : (
              <EmptyState
                icon="📋"
                title={t('event.noTasks.title')}
                description={t('event.noTasks.description')}
              />
            )}
          </SidebarContentWrapper>
        </>
      )}
    </SidebarContent>
  );
};

export default EventDetailsTasks;
